import * as React from 'react'
// We need to import this directly as the other hooks within the hooks folder
// are not SSR safe and causes the server to bundle these in.
import { useCookieConsentDecision } from 'hooks/useCookieConsentDecision'
import type {
  Cookie,
  CookiesPolicyProps,
  CookieSettingsLink
} from './CookiePolicy.types'

const storeTrackReason =
  'Speicherung und Verfolgung von Besuchen auf verschiedenen Websites.'

const cookieTypes = {
  marketing: 'Marketing/Tracking',
  functional: 'Funktional',
  analytical: 'Analytisch',
  statistics: 'Statistik'
}

const cookies: Cookie[] = [
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Bing Ads _uetvid',
    type: cookieTypes.marketing,
    duration: '1 Jahr',
    purpose: storeTrackReason
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Clarity _clsk',
    type: cookieTypes.marketing,
    duration: '1 Tag',
    purpose:
      'Speicherung und Zusammenfassung der Seitenaufrufe eines Benutzers in eine einzige Sitzungsaufzeichnung.'
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Bing Ads _uetsid',
    type: cookieTypes.marketing,
    duration: '1 Tag',
    purpose: storeTrackReason
  },
  {
    company: 'Laced',
    cookieName: 'rails_laced_app_session',
    type: cookieTypes.functional,
    duration: 'Sitzung',
    purpose:
      'um Besuche auf verschiedenen Websites zu speichern und zu verfolgen. Sind sie auf einem mobilen Gerät, Warenkorbnummer, Gast-ID, Sitzungs-ID, Checkout-ID, Suche'
  },
  {
    company: 'Laced',
    cookieName: '_laced_as',
    type: cookieTypes.analytical,
    duration: 'Sitzung',
    purpose:
      'Identifizierung von Produktsuchen, die in derselben Sitzung durchgeführt wurden. Wird verwendet, um die Reihenfolge der Suchergebnisse zu verbessern.'
  },
  {
    company: 'Laced',
    cookieName: '_laced_asqp',
    type: cookieTypes.analytical,
    duration: 'Sitzung',
    purpose:
      'Verfolgung der Position der ausgewählten Produkte auf der Suchergebnisseite. Wird verwendet, um die Reihenfolge der Suchergebnisse zu verbessern.'
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Clarity _clck',
    type: cookieTypes.marketing,
    duration: '1 Jahr',
    purpose: 'Speicherung einer eindeutigen Benutzer-ID.'
  },
  {
    company: 'Google',
    cookieName: 'Google Analytics _gid',
    type: cookieTypes.analytical,
    duration: '1 Tag',
    purpose: 'Speicherung und Zählung der Seitenaufrufe.'
  },
  {
    company: 'Stripe',
    cookieName: '__stripe_mid',
    type: cookieTypes.functional,
    duration: '1 Jahr',
    purpose: 'Vorbeugung gegen Betrug.'
  },
  {
    company: 'facebook',
    cookieName: '_fbp',
    type: cookieTypes.marketing,
    duration: '3 Monate',
    purpose: storeTrackReason
  },
  {
    company: 'Google',
    cookieName: 'Google Analytics _ga',
    type: cookieTypes.statistics,
    duration: '1 Jahr',
    purpose:
      'Bei diesem Cookie handelt es sich um einen dauerhaften Google-Analytics-Cookie, der zur Unterscheidung einzelner Nutzer verwendet wird.'
  },
  {
    company: 'Google',
    cookieName: 'Google Adsense  _gcl_au',
    type: cookieTypes.marketing,
    duration: '1 Jahr',
    purpose: 'Speicherung und Verfolgung von Conversions.'
  },
  {
    company: 'Laced',
    cookieName: '',
    type: '',
    duration: '',
    purpose: 'Cookie-Einstellungen'
  }
]

const cookieSettingsLinks: CookieSettingsLink[] = [
  {
    label: 'Microsoft Edge',
    href: 'https://support.microsoft.com/de-de/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09'
  },
  {
    label: 'Chrome',
    href: 'https://support.google.com/chrome/answer/95647?hl=de'
  },
  {
    label: 'Firefox',
    href: 'https://support.mozilla.org/de-DE/kb/delete-cookies-remove-info-websites-stored'
  },
  {
    label: 'Safari',
    href: 'https://support.apple.com/de-de/guide/safari/sfri11471/mac'
  }
]

const CookiesPolicyDe: React.FC<CookiesPolicyProps> = ({
  siteHostname,
  stylizedHostname
}) => {
  const { mutate, existingCookiePreferences: preferences } =
    useCookieConsentDecision()

  return (
    <div className="layout legal">
      <h1>Cookie-Richtlinie</h1>
      <p>
        Unsere Website,{' '}
        <a
          href={`https://${siteHostname}/`}
          target="_blank"
          rel="noreferrer noopener"
        >
          {stylizedHostname}
        </a>{' '}
        (unsere &quot;Website&quot;) verwendet Cookies, um Sie von anderen
        Benutzern unserer Website unterscheiden zu können. Dadurch können wir
        Ihnen während des Besuchs unserer Website eine angenehme
        Benutzererfahrung bieten. Außerdem können wir dadurch unsere Website
        verbessern. Wenn Sie unsere Website weiterhin nutzen, erklären Sie sich
        mit unserer Verwendung von Cookies einverstanden.
      </p>

      <h4>Was sind Cookies?</h4>
      <p>
        Ein Cookie ist eine kleine Datei, die aus Buchstaben und Zahlen besteht
        und die wir auf Ihrem Browser oder der Festplatte Ihres Computers
        speichern, wenn Sie dem zustimmen. Cookies sind entweder nur für die
        Dauer Ihres Besuchs auf unserer Website vorhanden oder für die Dauer
        Ihrer Internetnutzung oder auch für eine längere Dauer, wenn sie dazu
        dienen, Ihnen auf unserer Website ein maßgeschneidertes Surferlebnis
        oder einen maßgeschneiderten Service zu bieten.
      </p>

      <h4>Welche Cookies verwenden wir?</h4>
      <p>
        Wir verwenden die folgenden Cookies auf unserer Website für die jeweils
        beschriebenen Zwecke:
      </p>
      <div className="legal-table-wrapper">
        <table className="legal-table caption">
          <thead>
            <tr>
              <td>Unternehmen</td>
              <td>Cookie-Bezeichnung</td>
              <td>Art</td>
              <td>Dauer</td>
              <td>Zweck</td>
            </tr>
          </thead>
          <tbody>
            {cookies.map((cookie) => (
              <tr key={cookie.cookieName}>
                <td>{cookie.company}</td>
                <td>{cookie.cookieName}</td>
                <td>{cookie.type}</td>
                <td>{cookie.duration}</td>
                <td>{cookie.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Sie können Ihre{' '}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href=""
          onClick={(e) => {
            e.preventDefault()
            mutate({ ...preferences, valid: false }, { revalidate: false })
          }}
        >
          Cookie-Einstellungen
        </a>{' '}
        auf unserer Website jederzeit ändern.
      </p>
      <p>
        Die meisten Internet-Browsersoftwareprogramme ermöglichen es Ihnen, die
        Annahme und Speicherung von Cookies zu steuern. Sie können Cookies
        blockieren, indem Sie im Menü Ihres Browsers die Einstellung aktivieren,
        mit der Sie alle oder einige Cookies ablehnen können.
      </p>
      <p>
        Um Ihre Cookie-Einstellungen in Ihrem Webbrowser zu ändern, verwenden
        Sie bitte die folgenden Links:
      </p>
      <ul className="legal-disc-list">
        {cookieSettingsLinks.map((link) => (
          <li key={link.href}>
            Für{' '}
            <a href={link.href} target="_blank" rel="noreferrer noopener">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
      <p>
        Weitere Informationen über Cookies sowie ihre Steuerung und Verwaltung
        finden Sie in den Leitlinien des Information{' '}
        <a
          href="https://www.baden-wuerttemberg.datenschutz.de/faq-zu-cookies-und-tracking-2/"
          target="_blank"
          rel="noreferrer noopener"
        >
          German Local Guidance
        </a>
        .
      </p>
      <p className="legal-timestamp footnote dark">
        Diese Cookie-Richtlinie wurde zuletzt am 31. Juli 2023 aktualisiert.
      </p>
    </div>
  )
}

export default CookiesPolicyDe
